import React, { useState } from 'react';
import { useAnalytics } from '../../contexts/AnalyticsContext';
import { 
  Users, 
  BookOpen, 
  DollarSign, 
  TrendingUp, 
  Star,
  Download
} from 'lucide-react';

const AnalyticsDashboard = () => {
  const { 
    analytics, 
    loading, 
    getPerformanceMetrics, 
    getTopCourses, 
    getTopInstructors,
    getDemographics,
    exportAnalytics
  } = useAnalytics();
  
  const [selectedPeriod, setSelectedPeriod] = useState('month');

  const performanceMetrics = getPerformanceMetrics();
  const topCourses = getTopCourses(5);
  const topInstructors = getTopInstructors(5);
  const demographics = getDemographics();

  const handleExport = () => {
    const result = exportAnalytics('csv');
    if (result.success) {
      alert('Données exportées avec succès !');
    } else {
      alert('Erreur lors de l\'export');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-gray-600">Chargement des analytics...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header avec contrôles */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Analytics & Statistiques</h2>
          <p className="text-gray-600">Tableau de bord des performances de la plateforme</p>
        </div>
        
        <div className="flex items-center gap-3">
          <select
            value={selectedPeriod}
            onChange={(e) => setSelectedPeriod(e.target.value)}
            className="px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
          >
            <option value="week">Cette semaine</option>
            <option value="month">Ce mois</option>
            <option value="year">Cette année</option>
          </select>
          
          <button
            onClick={handleExport}
            className="flex items-center gap-2 bg-primary text-white px-4 py-2 rounded-lg hover:bg-primary/90 transition-colors duration-200"
          >
            <Download className="w-4 h-4" />
            Exporter
          </button>
        </div>
      </div>

      {/* Métriques principales */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Total Étudiants</p>
              <p className="text-3xl font-bold text-gray-900">{analytics.overview?.totalStudents || 0}</p>
              <p className="text-sm text-green-600">+{analytics.overview?.newStudentsThisMonth || 0} ce mois</p>
            </div>
            <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
              <Users className="w-6 h-6 text-blue-600" />
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Total Cours</p>
              <p className="text-3xl font-bold text-gray-900">{analytics.overview?.totalCourses || 0}</p>
              <p className="text-sm text-gray-500">Actifs</p>
            </div>
            <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center">
              <BookOpen className="w-6 h-6 text-green-600" />
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Revenus Totaux</p>
              <p className="text-3xl font-bold text-gray-900">€{analytics.overview?.totalRevenue?.toLocaleString() || 0}</p>
              <p className="text-sm text-green-600">+€{analytics.overview?.revenueThisMonth?.toLocaleString() || 0} ce mois</p>
            </div>
            <div className="w-12 h-12 bg-yellow-100 rounded-lg flex items-center justify-center">
              <DollarSign className="w-6 h-6 text-yellow-600" />
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Taux de Completion</p>
              <p className="text-3xl font-bold text-gray-900">{analytics.overview?.completionRate || 0}%</p>
              <p className="text-sm text-gray-500">Moyenne</p>
            </div>
            <div className="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center">
              <TrendingUp className="w-6 h-6 text-purple-600" />
            </div>
          </div>
        </div>
      </div>

      {/* Métriques de performance */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Performance</h3>
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <span className="text-sm text-gray-600">Note moyenne</span>
              <div className="flex items-center gap-1">
                <Star className="w-4 h-4 text-yellow-500" />
                <span className="font-semibold">{performanceMetrics.averageRating}</span>
              </div>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-sm text-gray-600">Taux de rebond</span>
              <span className="font-semibold">{performanceMetrics.bounceRate}%</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-sm text-gray-600">Durée moyenne</span>
              <span className="font-semibold">{performanceMetrics.averageSessionDuration}</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-sm text-gray-600">Pages par session</span>
              <span className="font-semibold">{performanceMetrics.pagesPerSession}</span>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Engagement</h3>
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <span className="text-sm text-gray-600">Utilisateurs actifs</span>
              <span className="font-semibold">{analytics.overview?.activeUsers || 0}</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-sm text-gray-600">Taux de conversion</span>
              <span className="font-semibold">{performanceMetrics.conversionRate}%</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-sm text-gray-600">Taux de completion</span>
              <span className="font-semibold">{performanceMetrics.completionRate}%</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-sm text-gray-600">Certificats émis</span>
              <span className="font-semibold">89.2%</span>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Démographie</h3>
          <div className="space-y-4">
            <div>
              <p className="text-sm text-gray-600 mb-2">Âges</p>
              <div className="space-y-1">
                {demographics.ageGroups?.slice(0, 3).map((group, index) => (
                  <div key={index} className="flex items-center justify-between text-sm">
                    <span>{group.range} ans</span>
                    <span className="font-semibold">{group.percentage}%</span>
                  </div>
                ))}
              </div>
            </div>
            <div>
              <p className="text-sm text-gray-600 mb-2">Pays</p>
              <div className="space-y-1">
                {demographics.countries?.slice(0, 3).map((country, index) => (
                  <div key={index} className="flex items-center justify-between text-sm">
                    <span>{country.name}</span>
                    <span className="font-semibold">{country.percentage}%</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Top cours et instructeurs */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Top Cours</h3>
          <div className="space-y-4">
            {topCourses.map((course, index) => (
              <div key={course.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 bg-primary rounded-full flex items-center justify-center">
                    <span className="text-white font-medium text-sm">{index + 1}</span>
                  </div>
                  <div>
                    <h4 className="font-medium text-gray-900">{course.title}</h4>
                    <p className="text-sm text-gray-600">{course.students} étudiants</p>
                  </div>
                </div>
                <div className="text-right">
                  <p className="font-semibold text-gray-900">€{course.revenue.toLocaleString()}</p>
                  <p className="text-sm text-gray-600">{course.completionRate}%</p>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Top Instructeurs</h3>
          <div className="space-y-4">
            {topInstructors.map((instructor, index) => (
              <div key={instructor.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 bg-primary rounded-full flex items-center justify-center">
                    <span className="text-white font-medium text-sm">{instructor.avatar}</span>
                  </div>
                  <div>
                    <h4 className="font-medium text-gray-900">{instructor.name}</h4>
                    <p className="text-sm text-gray-600">{instructor.courses} cours</p>
                  </div>
                </div>
                <div className="text-right">
                  <div className="flex items-center gap-1">
                    <Star className="w-4 h-4 text-yellow-500" />
                    <span className="font-semibold text-gray-900">{instructor.rating}</span>
                  </div>
                  <p className="text-sm text-gray-600">{instructor.students} étudiants</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Revenus par catégorie */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Revenus par Catégorie</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {analytics.courses?.categories?.map((category, index) => (
            <div key={index} className="p-4 bg-gray-50 rounded-lg">
              <div className="flex items-center justify-between mb-2">
                <h4 className="font-medium text-gray-900">{category.name}</h4>
                <span className="text-sm text-gray-600">{category.count} cours</span>
              </div>
              <div className="space-y-1">
                <div className="flex items-center justify-between text-sm">
                  <span className="text-gray-600">Revenus</span>
                  <span className="font-semibold">€{category.revenue.toLocaleString()}</span>
                </div>
                <div className="flex items-center justify-between text-sm">
                  <span className="text-gray-600">Étudiants</span>
                  <span className="font-semibold">{category.students}</span>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default AnalyticsDashboard;
